import type { IDraftDetail } from "@/api/clause-checklists/checklist-generation.types";
import { ChecklistGroupField } from "@/components/admin/dialogs/clause-checklists/ChecklistGroupField";
import { OrganizationAutocomplete } from "@/components/shared/OrganizationAutocomplete";
import { Button } from "@/components/ui/button";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { DraftPublicationGroupLabel } from "../../components/DraftPublicationGroupLabel";

export const DRAFT_GROUP_HELP =
    "When published, organizations in this group can use the checklist.";
export const ORGANIZATION_REFUSED =
    "This organization can't be used for this checklist. Choose another organization or remove it.";

export type PublishBlocker = "group_unavailable" | "no_destination";

const PUBLISH_BLOCKERS: Record<PublishBlocker, string> = {
    group_unavailable:
        "The selected group is no longer available. Choose another group or remove it before publishing.",
    no_destination: "Choose an organization or a group before publishing.",
};

interface IProps {
    draft: IDraftDetail;
    organizationId: number | null;
    onOrganizationChange: (id: number | null) => void;
    groupId: number | null;
    onGroupChange: (id: number | null) => void;
    organizationRefused: boolean;
    groupRefused: boolean;
    blocker: PublishBlocker | null;
    blockerId: string;
    disabled: boolean;
}

export const DraftPublishTargets = ({
    draft,
    organizationId,
    onOrganizationChange,
    groupId,
    onGroupChange,
    organizationRefused,
    groupRefused,
    blocker,
    blockerId,
    disabled,
}: IProps) => (
    <Stack spacing={1.5} minWidth={0}>
        <Stack spacing={0.5} minWidth={0}>
            <Typography variant="subtitle2" fontWeight={600}>
                Publish to
            </Typography>
            {groupId === draft.draft_publication_group_id &&
                draft.draft_publication_group_status !== "unavailable" && (
                    <DraftPublicationGroupLabel
                        draft={draft}
                        className="text-sm"
                    />
                )}
        </Stack>

        <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            gap={1}
            alignItems="flex-start"
            minWidth={0}
        >
            <Box sx={{ flex: "1 1 14rem", minWidth: 0 }}>
                <OrganizationAutocomplete
                    value={organizationId}
                    onChange={onOrganizationChange}
                    disabled={disabled}
                    error={organizationRefused}
                    helperText={
                        organizationRefused ? ORGANIZATION_REFUSED : undefined
                    }
                />
            </Box>
            {organizationId !== null && (
                <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    onClick={() => onOrganizationChange(null)}
                >
                    Remove organization
                </Button>
            )}
        </Stack>

        <ChecklistGroupField
            value={groupId}
            onChange={onGroupChange}
            refused={groupRefused}
            disabled={disabled}
            help={DRAFT_GROUP_HELP}
        />

        {blocker === "group_unavailable" && (
            <Alert severity="warning" role="status" id={blockerId}>
                {PUBLISH_BLOCKERS[blocker]}
            </Alert>
        )}
        {blocker !== null && blocker !== "group_unavailable" && (
            <Typography id={blockerId} variant="body2" color="text.secondary">
                {PUBLISH_BLOCKERS[blocker]}
            </Typography>
        )}
    </Stack>
);
